import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import {
    MessageSquare,
    Send,
    Edit2,
    Trash2,
    MoreHorizontal,
    Loader2,
    AtSign,
} from 'lucide-react';
import { useComments, useCreateComment, useUpdateComment, useDeleteComment } from '@/hooks/use-comments';
import { useProjectMembers } from '@/hooks/use-projects';
import { useTypingIndicator } from '@/hooks/use-realtime';
import { useAuthStore } from '@/stores/auth-store';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { MentionTextarea, type MentionUser } from '@/components/ui/mention-textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import type { Comment } from '@/lib/schemas';

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function formatCommentDate(dateString: string, language: string = 'en') {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    const locale = language === 'fr' ? fr : enUS;

    if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true, locale });
    }

    return format(date, 'MMM d, yyyy \'at\' h:mm a', { locale });
}

// ============================================
// SINGLE COMMENT
// ============================================

interface CommentItemProps {
    canEdit: boolean;
    comment: Comment;
    onEdit: (comment: Comment) => void;
    onDelete: (commentId: string) => void;
}

function CommentItem({ comment, onEdit, onDelete, canEdit }: CommentItemProps) {
    const { user } = useAuthStore();
    const { t, language } = useI18n();
    const isAuthor = canEdit && user?.id === comment.authorId;
    const isEdited = comment.createdAt !== comment.updatedAt;

    return (
        <div className="flex gap-3 p-3 rounded-lg hover:bg-surface-hover transition-colors group">
            <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={comment.author.avatarUrl || undefined} />
                <AvatarFallback className="bg-primary/20 text-primary text-xs">
                    {getInitials(comment.author.name)}
                </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground text-sm">
                            {comment.author.name}
                        </span>
                        <span className="text-xs text-foreground-muted">
                            {formatCommentDate(comment.createdAt, language)}
                        </span>
                        {isEdited && (
                            <span className="text-xs text-foreground-muted">({t('comments.edited')})</span>
                        )}
                    </div>

                    {isAuthor && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onEdit(comment);
                                    }}
                                >
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    {t('common.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onDelete(comment.id);
                                    }}
                                    className="text-destructive focus:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t('common.delete')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                <p className="text-sm text-foreground-secondary mt-1 whitespace-pre-wrap break-words">
                    {comment.content}
                </p>
            </div>
        </div>
    );
}

// ============================================
// COMMENT FORM
// ============================================

interface CommentFormProps {
    taskId: string;
    projectId: string;
    editingComment?: Comment | null;
    onCancel?: () => void;
    onSuccess?: () => void;
    onTypingStart?: () => void;
    onTypingStop?: () => void;
}

function CommentForm({ taskId, projectId, editingComment, onCancel, onSuccess, onTypingStart, onTypingStop }: CommentFormProps) {
    const { t } = useI18n();
    const { user } = useAuthStore();
    const [content, setContent] = useState(editingComment?.content || '');
    const [mentions, setMentions] = useState<string[]>([]);
    const createComment = useCreateComment(taskId);
    const updateComment = useUpdateComment(editingComment?.id || '', taskId);
    const { data: projectMembers } = useProjectMembers(projectId);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isLoading = createComment.isPending || updateComment.isPending;
    const isEditing = !!editingComment;

    // Convert project members to MentionUser format (exclude current user)
    const mentionUsers: MentionUser[] = useMemo(() => {
        return (projectMembers || [])
            .filter(member => member.userId !== user?.id)
            .map(member => ({
                id: member.userId,
                name: member.user.name,
                email: member.user.email,
                avatarUrl: member.user.avatarUrl,
            }));
    }, [projectMembers, user?.id]);

    // Update content when editingComment changes
    useEffect(() => {
        if (editingComment) {
            setContent(editingComment.content);
        } else {
            setContent('');
        }
    }, [editingComment]);

    // Clear typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            onTypingStop?.();
        };
    }, [onTypingStop]);

    const handleContentChange = useCallback((value: string) => {
        setContent(value);

        // Emit typing start event
        onTypingStart?.();

        // Clear existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Set timeout to emit typing stop after 2 seconds of inactivity
        typingTimeoutRef.current = setTimeout(() => {
            onTypingStop?.();
        }, 2000);
    }, [onTypingStart, onTypingStop]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!content.trim() || isLoading) return;

        // Stop typing indicator immediately on submit
        onTypingStop?.();
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        try {
            if (isEditing) {
                await updateComment.mutateAsync({ content: content.trim(), mentions });
            } else {
                await createComment.mutateAsync({ content: content.trim(), mentions });
            }
            setContent('');
            setMentions([]);
            onSuccess?.();
        } catch (error) {
            console.error('Error saving comment:', error);
        }
    }, [content, mentions, isLoading, isEditing, createComment, updateComment, onSuccess, onTypingStop]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Submit on Cmd/Ctrl + Enter
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSubmit(e);
        }
    }, [handleSubmit]);

    return (
        <form onSubmit={handleSubmit} className="space-y-2">
            <MentionTextarea
                users={mentionUsers}
                value={content}
                onChange={(value) => handleContentChange(value)}
                onMentionsChange={setMentions}
                onKeyDown={handleKeyDown}
                placeholder={isEditing ? t('comments.editPlaceholder') : t('comments.commentPlaceholder')}
                className="min-h-[80px] resize-none"
                disabled={isLoading}
            />
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-muted">
                        {t('comments.submitHint')}
                    </span>
                    {mentions.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-primary whitespace-nowrap">
                            <AtSign className="h-3 w-3" />
                            <span>{mentions.length} {t('comments.mentionCount')}</span>
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    {isEditing && (
                        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                            {t('common.cancel')}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        size="sm"
                        disabled={!content.trim() || isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-1" />
                                {isEditing ? t('comments.save') : t('comments.comment')}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </form>
    );
}

// ============================================
// COMMENTS SECTION
// ============================================

interface CommentsSectionProps {
    canEdit?: boolean;
    taskId: string;
    projectId: string;
}

export function CommentsSection({ taskId, projectId, canEdit = true }: CommentsSectionProps) {
    const { t } = useI18n();
    const { data, isLoading, error } = useComments(taskId);
    const deleteComment = useDeleteComment(taskId);
    const { typingUsers, emitTypingStart, emitTypingStop } = useTypingIndicator(canEdit ? taskId : '');

    const [editingComment, setEditingComment] = useState<Comment | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

    const handleEdit = useCallback((comment: Comment) => {
        setEditingComment(comment);
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingComment(null);
    }, []);

    const handleDeleteClick = useCallback((commentId: string) => {
        setCommentToDelete(commentId);
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!commentToDelete) return;

        try {
            await deleteComment.mutateAsync(commentToDelete);
            setDeleteDialogOpen(false);
            setCommentToDelete(null);
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    }, [commentToDelete, deleteComment]);

    const comments = data?.comments || [];

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-foreground-secondary">
                <MessageSquare className="h-4 w-4" />
                <h3 className="font-medium text-sm">
                    {t('tasks.comments')} {comments.length > 0 && `(${comments.length})`}
                </h3>
            </div>

            {/* Comment form */}
            {canEdit && (editingComment ? (
                <CommentForm
                    key={`edit-${editingComment.id}`}
                    taskId={taskId}
                    projectId={projectId}
                    editingComment={editingComment}
                    onCancel={handleCancelEdit}
                    onSuccess={handleCancelEdit}
                    onTypingStart={emitTypingStart}
                    onTypingStop={emitTypingStop}
                />
            ) : (
                <CommentForm
                    key="create"
                    taskId={taskId}
                    projectId={projectId}
                    onTypingStart={emitTypingStart}
                    onTypingStop={emitTypingStop}
                />
            ))}

            {/* Typing indicator */}
            {typingUsers.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-foreground-muted animate-pulse">
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="inline-block w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="inline-block w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span>
                        {typingUsers.length === 1
                            ? `${typingUsers[0]?.userName ?? 'Someone'} ${t('comments.isTyping')}`
                            : typingUsers.length === 2
                                ? `${typingUsers[0]?.userName ?? 'Someone'} & ${typingUsers[1]?.userName ?? 'someone'} ${t('comments.areTyping')}`
                                : `${typingUsers[0]?.userName ?? 'Someone'} ${t('comments.andOthersTyping').replace('{count}', String(typingUsers.length - 1))}`}
                    </span>
                </div>
            )}

            {/* Comments list */}
            <div className="space-y-1">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : error ? (
                    <div className="text-center py-8 text-destructive text-sm">
                        {t('comments.failedToLoad')}
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-foreground-muted text-sm">
                        {t('comments.noComments')} {t('comments.beFirstToComment')}
                    </div>
                ) : (
                    comments.map((comment) => (
                        <CommentItem
                            canEdit={canEdit}
                            key={comment.id}
                            comment={comment}
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                        />
                    ))
                )}
            </div>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('comments.deleteTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('comments.deleteConfirm')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteComment.isPending}
                        >
                            {deleteComment.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                t('common.delete')
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
